<template>
    <default-layout>
        <div class="row">
            <div class="col-12">
                <div class="btn-toolbar float-right" role="toolbar" aria-label="Distributors toolbar">
                    <div class="btn-group mr-2" role="group" aria-label="Distributors toolbar, first group">
                        <button type="button" class="btn-sm page-link mr-2" @click="onRefresh">
                            <font-awesome-icon class="mr-1" icon="sync"/>
                            Refresh
                        </button>
                        <button type="button" class="btn-sm page-link" @click="exportCsv" title="Export CSV">
                            <font-awesome-icon class="mr-1" icon="file-csv"/>
                            Export CSV
                        </button>
                    </div>
                </div>
                <h1 class="h4 mb-4 clearfix">
                    Distributor licenses
                    <span class="badge badge-pill badge-primary" v-if="this.licenses.total">{{ this.licenses.total }}</span>
                </h1>
            </div>
        </div>

        <div class="row" v-if="loading === true">
            <div class="col-12">
                <spinner-component class="p-5" type="spinner-grow"/>
            </div>
        </div>

        <div class="row" v-if="loading === false">

            <div class="col-12">
                <form class="form-inline mb-4" @submit.prevent="onRefresh">
                    <div class="form-group mr-2">
                        <input class="form-control form-control-sm" type="text" placeholder="Search..." v-model="term">
                    </div>
                    <button type="submit" class="btn btn-sm btn-primary">Search</button>
                </form>
            </div>

            <div class="col-12 clearfix">
                <pagination-component ref="paginationComponent"
                                      :page="page"
                                      :limit="limit"
                                      :total="licenses.total"
                                      @paginate="onChangePage"/>

                <div class="btn-toolbar float-right" role="toolbar" aria-label="Distributor table per page">
                    <div class="btn-group mr-2" role="group" aria-label="Distributors toolbar, first group">
                        <form class="form-inline mb-4" @change="onRefresh">
                            <div class="form-group">
                                <label class="mr-2" for="customers-per-page">
                                    <small>Reseller licenses per page</small>
                                </label>
                                <select id="customers-per-page" class="form-control form-control-sm" v-model="limit" @change="onChangeLimit">
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="75">75</option>
                                    <option value="100">100</option>
                                    <option value="500">500</option>
                                    <option value="999999999">All</option>
                                </select>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div class="col-12 clearfix">
                <div class="btn-toolbar float-right mb-4" role="toolbar">
                    <div class="btn-group mr-2" role="group" aria-label="Distributor licenses actions">
                        <div class="form-group mb-0">
                            <label class="sr-only" for="distributor-licenses-bulk-actions">
                                <small>Bulk action</small>
                            </label>
                            <select id="distributor-licenses-bulk-actions" class="form-control form-control-sm" v-model="bulkAction">
                                <option value="create-quick-view-links">Create quick view links</option>
                            </select>
                        </div>
                    </div>
                    <div class="btn-group" role="group" aria-label="Distributor licenses actions">
                        <button class="btn btn-group-sm btn-sm btn-secondary"
                                title="Submit"
                                data-toggle="modal"
                                data-target="#distributor-details-bulk-actions-modal">
                            Submit
                        </button>
                    </div>
                </div>
            </div>

            <div class="col-12">
                <div class="table-responsive">
                    <table class="table">
                        <thead>
                        <th scope="col"><input type="checkbox" v-model="selectAll"></th>
                        <th scope="col">License Code</th>
                        <th scope="col">Quick View Link</th>
                        <th scope="col">Type</th>
                        <th scope="col">Status</th>
                        <th scope="col">Actions</th>
                        </thead>
                        <tbody>
                        <tr v-for="license in this.licenses.list" :key="license.id">
                            <td class="align-middle">
                                <input type="checkbox" v-model="selected" :value="license.id">
                            </td>
                            <td class="align-middle">{{ license.licenseCode }}</td>
                            <td class="align-middle">
                                <div v-if="license.licenseQuickviewLink" class="text-success">
                                    <button class="page-link small"
                                            tabindex="0"
                                            role="button"
                                            @click="onCopyToClipboard(license.licenseQuickviewLink)"
                                            data-container="body"
                                            data-toggle="popover"
                                            data-trigger="focus"
                                            data-placement="top"
                                            data-content="Copied!">
                                        <font-awesome-icon class="mr-1" icon="clipboard"/>
                                        Copy to clipboard
                                    </button>
                                </div>
                            </td>
                            <td>
                                <span class="badge badge-primary">Reseller</span>
                            </td>
                            <td>
                                <span :class="getLicenseStatusClass(license.status)">
                                    {{ getLicenseStatusLabel(license.status) }}
                                </span>
                            </td>
                            <td>
                                <button class="btn btn-sm btn-outline-danger mr-2" @click="onDeleteLicense(license.id)" title="Delete license">
                                    <font-awesome-icon icon="trash"/>
                                </button>

                                <a class="btn btn-sm btn-outline-info"
                                   title="License details"
                                   :href="'/licenses/' + license.id"
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   v-if="license.type === 'LICENSE_DEFAULT' && (license.status === 'LICENSE_WATCHING' || license.status === 'LICENSE_USED')">
                                    <font-awesome-icon icon="info-circle" class="mr-1"/>
                                    Details
                                </a>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Bulk actions -->
        <modal-component id="distributor-details-bulk-actions-modal"
                         class="fade"
                         title="Bulk actions">
            <div class="modal-body">
                <!-- MODAL SPINNER -->
                <div class="row mb-3 justify-content-center" v-if="modal.loading === true">
                    <spinner-component class="p-5" type="spinner-grow"/>
                </div>

                <div class="row" v-if="modal.loading === false">
                    <div class="col-12">
                        <form @submit.prevent="onSubmitBulkAction">
                            <div class="form-group">
                                <label for="quick-create-domain">Domain URL</label>
                                <input id="quick-create-domain" class="form-control" type="text" v-model="domain">
                            </div>

                            <p class="text-muted">{{ selected.length }} license(s) have been selected.</p>

                            <button type="submit" class="btn btn-primary">Submit</button>
                        </form>
                    </div>
                </div>
            </div>
        </modal-component>

    </default-layout>
</template>

<script>
import DefaultLayout from "@/components/layout/DefaultLayout";
import SpinnerComponent from "@/components/ui/SpinnerComponent";
import getLicenseStatusClass from "@/helpers/get-license-status-class";
import getLicenseStatusLabel from "@/helpers/get-license-status-label";
import PaginationComponent from "@/components/PaginationComponent";
import serviceEnv from "@/services/env.js";
import ModalComponent from "@/components/ui/ModalComponent";
import moment from "moment";
import $ from "jquery";

export default {
    name: "DistributorDetailsPage",
    components: { ModalComponent, PaginationComponent, SpinnerComponent, DefaultLayout },
    computed: {
        cliffMarketModule() {
            return this.$store.state.cliffMarket;
        },
        customer() {
            return this.$store.state.cliffMarket.customer;
        },
        customerId() {
            let customerId = null;

            if (Object.prototype.hasOwnProperty.call(this.$route.params, 'customerId')) {
                customerId = this.$route.params.customerId;
                customerId = customerId ? customerId : null;
            }

            return customerId;
        },
        resellerLicenses() {
            let resellerLicenses = [];

            this.customer.licenses.forEach(license => {
                if (license.type === 'LICENSE_RESELLER') {
                    resellerLicenses.push(license);
                }
            });

            return resellerLicenses;
        },
        selectAll: {
            get() {
                return this.licenses.list ? this.selected.length === this.licenses.list.length : false;
            },
            set(value) {
                let selected = [];

                if (value) {
                    this.licenses.list.forEach(function(license) {
                        selected.push(license.id);
                    });
                }

                this.selected = selected;
            }
        }
    },
    data() {
        return {
            page: 1,
            limit: 10,
            term: '',
            selected: [],
            loading: true,
            domain: '',
            bulkAction: 'create-quick-view-links',
            licenses: {
                total: 0,
                list: []
            },
            modal: {
                loading: false
            }
        }
    },
    methods: {
        getLicenseStatusClass(licenseStatus) {
            return getLicenseStatusClass(licenseStatus);
        },
        getLicenseStatusLabel(licenseStatus) {
            return getLicenseStatusLabel(licenseStatus);
        },
        loadResellerLicenses(page, limit, term) {
            this.loading = true;

            this.licenses =  {
                total: 0,
                list: []
            };

            const start = (page - 1) * limit;
            const end = page * limit;
            let filteredLicenses = this.resellerLicenses;

            if (term) {
                filteredLicenses = filteredLicenses.filter(license => {
                    return license.licenseCode.toUpperCase().includes(term.toUpperCase());
                });
            }

            let total = filteredLicenses.length;
            let licenses = filteredLicenses.slice(start, end);

            this.licenses = {
                total: total,
                list: licenses
            };

            this.loading = false;
        },
        async onRefresh() {
            this.loadResellerLicenses(this.page, this.limit, this.term);
        },
        onChangePage(page) {
            if (page <= 0) {
                return;
            }

            this.page = parseInt(page);
            this.onRefresh();
        },
        onChangeLimit() {
            this.page = 1;
            this.$refs.paginationComponent.onChangePage(1);
        },
        onCopyToClipboard(text) {
            const el = document.createElement('textarea');
            el.value = text;
            el.setAttribute('readonly', '');
            el.style.position = 'absolute';
            el.style.left = '-9999px';
            document.body.appendChild(el);
            const selected =  document.getSelection().rangeCount > 0  ? document.getSelection().getRangeAt(0) : false;
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);

            if (selected) {
                document.getSelection().removeAllRanges();
                document.getSelection().addRange(selected);
            }
        },
        async onSubmitBulkAction() {
            this.modal.loading = true;
            let licenses = [];

            this.selected.forEach(function(license) {
                licenses.push({
                    id: license
                });
            })

            const payload = {
                domain: this.domain,
                parameters: {
                    lang: 'es'
                },
                licenses: licenses
            };

            await this.$store.dispatch('interactiveLink/createQuickViewUrl', payload);
            await this.$store.dispatch('cliffMarket/getCustomer', this.customerId);
            await this.onRefresh();

            this.modal.loading = false;
        },
        exportCsv() {
            let exportData = [];
            let csvContent = "data:text/csv;charset=utf-8,";

            this.licenses.list.forEach(license => {
                exportData.push({
                    LICENSE_ID: license.id,
                    LICENSE_CODE: license.licenseCode,
                    LICENSE_QUICK_VIEW_LINK: license.licenseQuickviewLink,
                    LICENSE_STATUS: license.status,
                    LICENSE_TYPE: license.type,
                    LICENSE_PURCHASE_DATE: license.purchaseDate,
                    LICENSE_ACTIVATION_DATE: license.activationDate,
                    LICENSE_ORDER_ID: license.order,
                    LICENSE_PRODUCT_ID: license.product,
                    LICENSE_PROJECT_ID: license.project,
                    LICENSE_RESELLER_LICENSE_COUNT: license.resellerLicenseCount,
                });
            });

            csvContent += [
                Object.keys(exportData[0]).join(";"),
                ...exportData.map(license => Object.values(license).join(";"))
            ]
                .join("\n")
                .replace(/(^\[)|(\]$)/gm, "");

            const data = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", data);
            link.setAttribute("download", "interactive-link-export-licenses-" + moment().format('YYYYMMDD-HHMMSS') + ".csv");
            link.click();
        }
    },
    async beforeMount() {
        if (this.customerId !== null) {
            await this.$store.dispatch('cliffMarket/getCustomer', this.customerId);
        }

        this.domain = serviceEnv.config.websiteUrl + '/partners/latam';

        await this.onRefresh();

        $('[data-toggle="popover"]').popover({
            trigger: 'focus'
        });

        this.loading = false;
    },
    mounted() {
    }
}
</script>

<style lang="scss" scoped>
.quick-view-url {
    small {
        display: block;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>